import { Button, IconButton } from "@hortis/ui/button";
import {
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from "@hortis/ui/dialog";
import { Download1, XClose } from "@hortis/ui/icons";
import { toast } from "@hortis/ui/sonner";
import { Tooltip, TooltipContent, TooltipTrigger } from "@hortis/ui/tooltip";
import { QRCodeCanvas, QRCodeSVG } from "qrcode.react";
import { useRef } from "react";
import { createMaterialNumber } from "src/features/collection/components/plant-materials/material-number";
import { stopEventPropagation } from "src/features/collection/components/stop-event-propagation";
import { usePlaceBasic } from "src/utils/hooks/place";

export const PublicQRCodeDialogContent = ({
  materialId,
  accessionNumber,
  qualifier,
  scientificName,
}: {
  materialId: string;
  accessionNumber: string;
  qualifier: string;
  scientificName: string;
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const place = usePlaceBasic();

  const downloadPngQRCode = () => {
    if (canvasRef.current == null) {
      toast.error("Failed to download QR code");
      return;
    }
    const imageURL = canvasRef.current.toDataURL("image/png");
    const link = document.createElement("a");
    link.href = imageURL;
    link.download = `QR_plant_${accessionNumber}-${qualifier}.png`;
    link.click();
  };

  const downloadSvgQRCode = () => {
    if (svgRef.current == null) {
      toast.error("Failed to download QR code");
      return;
    }
    const svgData = new XMLSerializer().serializeToString(svgRef.current);
    const blob = new Blob([svgData], {
      type: "image/svg+xml;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `QR_plant_${accessionNumber}-${qualifier}.svg`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const publicPageUrl =
    place == null
      ? null
      : `/public/site/${place.siteSlug}/map/plant/${materialId}`;

  return (
    <DialogContent className="gap-6! max-w-xs!">
      <DialogTitle>Public QR Code</DialogTitle>
      <DialogClose asChild className="absolute right-4 top-4">
        <IconButton
          icon={XClose}
          size="sm"
          variant="tertiaryGray"
          ariaLabel="Close"
          data-cy="close-qr-code-dialog"
        />
      </DialogClose>
      <div className="flex items-center gap-3 flex-col">
        <div className="p-4 border-grey-200 shadow-xs rounded-2xl border">
          <QRCodeCanvas
            value={`${window.location.origin}/${publicPageUrl}`}
            className="h-36 w-36 hidden"
            fgColor="#1f2a37"
            ref={canvasRef}
            size={1024}
          />
          <QRCodeSVG
            value={`${window.location.origin}/${publicPageUrl}`}
            className="h-36 w-36"
            fgColor="#1f2a37"
            ref={svgRef}
            data-cy="qr-code-svg"
            data-url={`${window.location.origin}/${publicPageUrl}`}
          />
        </div>
        <div className="text-sm space-y-0.5 text-center">
          <div
            className="font-medium text-grey-800"
            data-cy="qr-code-scientific-name"
          >
            {scientificName}
          </div>
          <div className="text-grey-500" data-cy="qr-code-material-number">
            {createMaterialNumber(accessionNumber, qualifier, "/")}
          </div>
        </div>
      </div>
      <DialogFooter>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              startIcon={Download1}
              size="sm"
              fullWidth
              onClick={downloadPngQRCode}
              testId="download-png-qr-code"
              onFocusCapture={stopEventPropagation}
            >
              PNG
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">Download PNG</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              startIcon={Download1}
              size="sm"
              fullWidth
              onClick={downloadSvgQRCode}
              testId="download-svg-qr-code"
              onFocusCapture={stopEventPropagation}
            >
              SVG
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">Download SVG</TooltipContent>
        </Tooltip>
      </DialogFooter>
    </DialogContent>
  );
};
